import { Alert, AnimatedContentLoader, Button, HeaderBlock, Prompt } from '@components'
import { PermissionBoundary, PermissionSet, useBusinessContext, usePermission } from '@context'
import { useQueryParam } from '@hooks'
import { deleteAccount, fetchAccount, hideAccount, IAccount } from '@query'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaEyeSlash, FaPen, FaTrash } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { AccountFormModal } from './AccountFormModal'
import { AccountOverview } from './AccountOverview'

interface Props {
  accountId: number
}

export const AccountDetails: React.FC<Props> = ({ accountId }) => {
  const [showEdit, setShowEdit] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const { slug } = useBusinessContext()
  const [, setAccountId] = useQueryParam('id')
  const queryClient = useQueryClient()
  const [t] = useTranslation()
  const hasEditPermission = usePermission(PermissionSet.ACCOUNTS_EDIT)

  const { data, isLoading } = useQuery<IAccount>({
    queryKey: [slug, 'accounts', accountId],
    queryFn: () => fetchAccount({ slug, accountId })
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteAccount({ slug, accountId }),
    ...{
      onSuccess: async () => {
        setAccountId(undefined)
        setShowDelete(false)

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [slug, 'accounts'] }),
          queryClient.invalidateQueries({ queryKey: [slug, 'tabular_accounts'] }),
          queryClient.invalidateQueries({ queryKey: [slug, 'auto_complete_accounts'] })
        ])
      },
      onError: () => {
        setShowDelete(false)
        toast.error(t('error.genericRetry'))
      }
    }
  })

  const hideMutation = useMutation({
    mutationFn: () => hideAccount({ slug, accountId }),
    ...{
      onSuccess: async () => {
        setAccountId(undefined)
        setShowDelete(false)

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [slug, 'accounts'] }),
          queryClient.invalidateQueries({ queryKey: [slug, 'tabular_accounts'] }),
          queryClient.invalidateQueries({ queryKey: [slug, 'auto_complete_accounts'] })
        ])
      },
      onError: () => {
        setShowDelete(false)
        toast.error(t('error.genericRetry'))
      }
    }
  })

  const renderAlert = () => {
    if (data?.is_used) {
      return (
        <Alert
          type="warning"
          title={t('accounts.delete.blockedTitleUsed')}
          description={t('accounts.delete.blockedDescriptionUsed')}
          isVisible={true}
        />
      )
    }

    return null
  }

  return (
    <>
      <StyledDetails key={accountId}>
        <AnimatedContentLoader isLoading={isLoading}>
          <DetailWrapper>
            {!isLoading && (
              <HeaderBlock
                header={`${data?.number} ${data?.name}`}
                subHeader={<></>}
                menuItems={
                  hasEditPermission && [
                    {
                      name: t('accounts.hideAccount'),
                      Icon: FaEyeSlash,
                      onClick: () => hideMutation.mutateAsync()
                    },
                    {
                      name: t('accounts.deleteAccount'),
                      Icon: FaTrash,
                      onClick: () => setShowDelete(true),
                      intent: 'danger'
                    }
                  ]
                }
              />
            )}

            <ScrollableContent>{data && <AccountOverview account={data} />}</ScrollableContent>

            <PermissionBoundary required={PermissionSet.ACCOUNTS_EDIT}>
              <StyledFooter>
                <Button icon={<FaPen />} onClick={() => setShowEdit(true)}>
                  {t('accounts.editAccount')}
                </Button>
              </StyledFooter>
            </PermissionBoundary>
          </DetailWrapper>
        </AnimatedContentLoader>
      </StyledDetails>

      <AccountFormModal
        account={data}
        isVisible={showEdit}
        handleOnClose={() => setShowEdit(false)}
      />

      <Prompt
        isVisible={showDelete}
        title={t('accounts.delete.title')}
        description={
          <>
            {renderAlert()}
            <div>{t('accounts.delete.description', { name: data?.name })}</div>
          </>
        }
        onClose={() => setShowDelete(false)}
        buttons={[
          {
            text: t('general.cancel'),
            intent: 'default',
            action: () => setShowDelete(false)
          },
          {
            text: t('accounts.deleteAccount'),
            icon: <FaTrash />,
            intent: 'danger',
            disabled: data?.is_used,
            action: async () => {
              await deleteMutation.mutateAsync()
            }
          }
        ]}
      />
    </>
  )
}

const StyledDetails = styled.div`
  height: 100%;
  width: 100%;
  background: white;
`

const DetailWrapper = styled.div`
  height: 100%;
  width: 100%;
  background: white;
  display: flex;
  flex-direction: column;
`

const ScrollableContent = styled.div`
  flex: 1;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing.md}rem;
  padding-top: 0;
`

const StyledFooter = styled.div`
  padding: ${({ theme }) => theme.spacing.md}rem;
`
